/**
 * CCMSProvider
 * React Provider component for CCMS SDK
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { CCMSContext } from './hooks/useCCMS';
import { HttpClient } from '../core/HttpClient';
import { PresenceClient } from '../realtime/presence/PresenceClient';
import { StockClient } from '../realtime/stock/StockClient';
import { createTaskServices } from '../services/task';
import { createUsersServices } from '../services/users';
import { createFileServices } from '../services/file';
import { createCommunicationServices } from '../services/communication';
import { createSupplierServices } from '../services/suppliers';
import { createSystemServices } from '../services/system';
import { createResourcesServices } from '../services/resources';
import { createProcurementsServices } from '../services/procurements';
import { createProjectsServices } from '../services/projects';
import { createHrServices } from '../services/hr';
import { createFeedbackServices } from '../services/feedback';
import { createDepartmentsServices } from '../services/departments';
import { createGeneratorsServices } from '../services/generators';
import { createProductsServices } from '../services/products';
import { createPaymentServices } from '../services/payments';
import { createOrderServices } from '../services/orders';
import { createMemberServices } from '../services/members';
import { createMeetingServices } from '../services/meetings';
import { createLocationsServices } from '../services/locations';
import { createSocialMediaServices } from '../services/socialmedia';
import { createVendorServices } from '../services/vendors';
import { createEventServices } from '../services/events';
import { createVendorRatingServices } from '../services/vendorrating';
import { createVendorCatalogServices } from '../services/vendorcatalog';
import { createVendorPortalServices } from '../services/vendor-portal';
import { createTaskPortalServices } from '../services/task-portal';
import { createStaffPortalServices } from '../services/staff-portal';
import { createAnnouncementServices } from '../services/announcements';
import { createBookingServices } from '../services/bookings';
import { createACCourseServices } from '../services/accourses';
import { createACCounsellingServices } from '../services/accounselling';
import { createAssetManagementServices } from '../services/assetmanagement';
import { createBudgetsServices } from '../services/budgets';
import { createFormsServices } from '../services/forms';
import { createRidesServices } from '../services/rides';
import { createProceduresServices } from '../services/procedures';
import { createCallCentreServices } from '../services/callcentre';
import { createSalvationServices } from '../services/salvation';
import { createGoogleServices } from '../services/google';
import { createAiServices } from '../services/ai';
import { createKnowledgeBaseServices } from '../services/knowledgebase';
import { createAnalyticsServices } from '../services/analytics';
import { createApplicationCatalogueServices } from '../services/applicationcatalogue';
import { createConnectorServices } from '../services/connector';
import { createApiKeysServices } from '../services/apikeys';
import { createAnatomyServices } from '../services/anatomy';
/**
 * CCMSProvider Component
 * Provides CCMS SDK context to child components
 */
export function CCMSProvider({ config, services: servicesConfig, children, onConnectionChange, onConnectionError, }) {
    const httpClientRef = useRef(null);
    const presenceClientRef = useRef(null);
    const stockClientRef = useRef(null);
    // Create/update HTTP client
    const httpClient = useMemo(() => {
        if (httpClientRef.current) {
            // Update existing client configuration
            httpClientRef.current.setBaseUrl(config.baseUrl);
            if (config.timeout) {
                httpClientRef.current.setTimeout(config.timeout);
            }
            return httpClientRef.current;
        }
        // Create new client
        const client = new HttpClient({
            baseUrl: config.baseUrl,
            timeout: config.timeout,
        });
        httpClientRef.current = client;
        return client;
    }, [config.baseUrl, config.timeout]);
    // Create Presence client if presenceUrl is provided
    const presenceClient = useMemo(() => {
        if (!config.presenceUrl) {
            return null; // Presence disabled
        }
        if (presenceClientRef.current) {
            return presenceClientRef.current;
        }
        // Construct full hub URL (auto-append /presencehub)
        const hubUrl = `${config.presenceUrl}/presencehub`;
        // Create client with token factory
        const client = new PresenceClient(hubUrl, async () => {
            if (config.getToken) {
                return await config.getToken();
            }
            return null;
        }, config.presenceOptions);
        presenceClientRef.current = client;
        return client;
    }, [config.presenceUrl, config.getToken, config.presenceOptions]);
    // Auto-connect presence when token becomes available
    useEffect(() => {
        if (!presenceClient)
            return;
        let isActive = true;
        const connectPresence = async () => {
            try {
                const token = config.getToken ? await config.getToken() : null;
                if (token && isActive) {
                    await presenceClient.connect();
                    if (config.debug) {
                        console.log('[CCMSProvider] Presence connected successfully');
                    }
                    onConnectionChange?.(true);
                }
            }
            catch (error) {
                if (config.debug) {
                    console.error('[CCMSProvider] Presence connection failed:', error);
                }
                onConnectionError?.(error);
            }
        };
        connectPresence();
        return () => {
            isActive = false;
            if (presenceClient) {
                presenceClient.disconnect();
            }
        };
    }, [presenceClient, config.getToken, config.debug, onConnectionChange, onConnectionError]);
    // Create Stock client if stockHubUrl is provided (anonymous, no auto-connect)
    const stockClient = useMemo(() => {
        if (!config.stockHubUrl) {
            return null; // Stock real-time disabled
        }
        if (stockClientRef.current) {
            return stockClientRef.current;
        }
        // Construct full hub URL (auto-append /stockhub)
        const hubUrl = `${config.stockHubUrl}/stockhub`;
        // Anonymous connection — no token required (shop is public)
        const client = new StockClient(hubUrl, config.stockOptions);
        stockClientRef.current = client;
        return client;
    }, [config.stockHubUrl, config.stockOptions]);
    // Subscribe to presence connection state changes
    useEffect(() => {
        if (!presenceClient || !onConnectionChange)
            return;
        const handleStateChange = (state) => {
            const connected = state === 'Connected';
            onConnectionChange(connected);
            if (config.debug) {
                console.log(`[CCMSProvider] Presence state changed: ${state}`);
            }
        };
        // Subscribe to state changes
        const unsubscribe = presenceClient.onStateChange(handleStateChange);
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [presenceClient, onConnectionChange, config.debug]);
    // Create services collection
    const services = useMemo(() => {
        const result = {};
        // Create task services if configured or by default
        if (servicesConfig?.task !== undefined || servicesConfig === undefined) {
            result.task = createTaskServices(httpClient, servicesConfig?.task);
        }
        // Create users services if configured or by default
        if (servicesConfig?.users !== undefined || servicesConfig === undefined) {
            result.users = createUsersServices(httpClient, servicesConfig?.users);
        }
        // Create file services if configured or by default
        if (servicesConfig?.file !== undefined || servicesConfig === undefined) {
            result.file = createFileServices(httpClient, servicesConfig?.file);
        }
        // Create communication services if configured or by default
        if (servicesConfig?.communication !== undefined || servicesConfig === undefined) {
            result.communication = createCommunicationServices(httpClient, servicesConfig?.communication);
        }
        // Create supplier services if configured or by default
        if (servicesConfig?.supplier !== undefined || servicesConfig === undefined) {
            result.supplier = createSupplierServices(httpClient, servicesConfig?.supplier);
        }
        // Create system services if configured or by default
        if (servicesConfig?.system !== undefined || servicesConfig === undefined) {
            result.system = createSystemServices(httpClient, servicesConfig?.system);
        }
        // Create resources services if configured or by default
        if (servicesConfig?.resources !== undefined || servicesConfig === undefined) {
            result.resources = createResourcesServices(httpClient, servicesConfig?.resources);
        }
        // Create procurement services if configured or by default
        if (servicesConfig?.procurements !== undefined ||
            servicesConfig?.procurement !== undefined ||
            servicesConfig === undefined) {
            result.procurements = createProcurementsServices(httpClient, servicesConfig?.procurements ?? servicesConfig?.procurement);
        }
        // Create projects services if configured or by default
        if (servicesConfig?.projects !== undefined || servicesConfig === undefined) {
            result.projects = createProjectsServices(httpClient, servicesConfig?.projects);
        }
        // Create products services if configured or by default
        if (servicesConfig?.products !== undefined || servicesConfig === undefined) {
            result.products = createProductsServices(httpClient, servicesConfig?.products);
        }
        // Create HR services if configured or by default
        if (servicesConfig?.hr !== undefined || servicesConfig === undefined) {
            result.hr = createHrServices(httpClient, servicesConfig?.hr);
        }
        // Create feedback services if configured or by default
        if (servicesConfig?.feedback !== undefined || servicesConfig === undefined) {
            result.feedback = createFeedbackServices(httpClient, servicesConfig?.feedback);
        }
        // Create departments services if configured or by default
        if (servicesConfig?.departments !== undefined || servicesConfig === undefined) {
            result.departments = createDepartmentsServices(httpClient, servicesConfig?.departments);
        }
        // Create generators services if configured or by default
        if (servicesConfig?.generators !== undefined || servicesConfig === undefined) {
            result.generators = createGeneratorsServices(httpClient, servicesConfig?.generators);
        }
        // Create payment services if configured or by default
        if (servicesConfig?.payments !== undefined ||
            servicesConfig?.payment !== undefined ||
            servicesConfig === undefined) {
            result.payments = createPaymentServices(httpClient, servicesConfig?.payments ?? servicesConfig?.payment);
        }
        // Create order services if configured or by default
        if (servicesConfig?.orders !== undefined ||
            servicesConfig?.order !== undefined ||
            servicesConfig === undefined) {
            result.orders = createOrderServices(httpClient, servicesConfig?.orders ?? servicesConfig?.order);
        }
        // Create member services if configured or by default
        if (servicesConfig?.members !== undefined ||
            servicesConfig?.member !== undefined ||
            servicesConfig === undefined) {
            result.members = createMemberServices(httpClient, servicesConfig?.members ?? servicesConfig?.member);
        }
        // Create meeting services if configured or by default
        if (servicesConfig?.meetings !== undefined ||
            servicesConfig?.meeting !== undefined ||
            servicesConfig === undefined) {
            result.meetings = createMeetingServices(httpClient, servicesConfig?.meetings ?? servicesConfig?.meeting);
        }
        // Create locations services if configured or by default
        if (servicesConfig?.location !== undefined || servicesConfig === undefined) {
            result.locations = createLocationsServices(httpClient, servicesConfig?.location);
        }
        // Create social media services if configured or by default
        if (servicesConfig?.socialmedia !== undefined || servicesConfig === undefined) {
            result.socialmedia = createSocialMediaServices(httpClient, servicesConfig?.socialmedia);
        }
        // Create events services if configured or by default
        if (servicesConfig?.events !== undefined || servicesConfig === undefined) {
            result.events = createEventServices(httpClient, servicesConfig?.events);
        }
        // Create vendor services if configured or by default
        if (servicesConfig?.vendors !== undefined || servicesConfig === undefined) {
            result.vendors = createVendorServices(httpClient, servicesConfig?.vendors);
        }
        // Create vendor rating services if configured or by default
        if (servicesConfig?.vendorrating !== undefined || servicesConfig === undefined) {
            result.vendorrating = createVendorRatingServices(httpClient, servicesConfig?.vendorrating);
        }
        // Create vendor catalog services if configured or by default
        if (servicesConfig?.vendorcatalog !== undefined || servicesConfig === undefined) {
            result.vendorcatalog = createVendorCatalogServices(httpClient, servicesConfig?.vendorcatalog);
        }
        // Create vendor portal services if configured or by default
        if (servicesConfig?.vendorPortal !== undefined || servicesConfig === undefined) {
            result.vendorPortal = createVendorPortalServices(httpClient, servicesConfig?.vendorPortal);
        }
        // Create task portal services if configured or by default
        if (servicesConfig?.taskPortal !== undefined || servicesConfig === undefined) {
            result.taskPortal = createTaskPortalServices(httpClient, servicesConfig?.taskPortal);
        }
        // Create staff portal services if configured or by default
        if (servicesConfig?.staffPortal !== undefined || servicesConfig === undefined) {
            result.staffPortal = createStaffPortalServices(httpClient, servicesConfig?.staffPortal);
        }
        // Create announcement services if configured or by default
        if (servicesConfig?.announcements !== undefined || servicesConfig === undefined) {
            result.announcements = createAnnouncementServices(httpClient, servicesConfig?.announcements);
        }
        // Create booking services if configured or by default
        if (servicesConfig?.bookings !== undefined || servicesConfig === undefined) {
            result.bookings = createBookingServices(httpClient, servicesConfig?.bookings);
        }
        // Create ACCourses (Course Management) services if configured or by default
        if (servicesConfig?.acCourses !== undefined || servicesConfig === undefined) {
            result.acCourses = createACCourseServices(httpClient, servicesConfig?.acCourses);
        }
        // Create ACCounselling (Care/Counselling Management) services if configured or by default
        if (servicesConfig?.acCounselling !== undefined || servicesConfig === undefined) {
            result.acCounselling = createACCounsellingServices(httpClient, servicesConfig?.acCounselling);
        }
        // Create Asset Management services if configured or by default
        if (servicesConfig?.assetManagement !== undefined || servicesConfig === undefined) {
            result.assetManagement = createAssetManagementServices(httpClient, servicesConfig?.assetManagement);
        }
        // Create Budget Management services if configured or by default
        if (servicesConfig?.budgets !== undefined || servicesConfig === undefined) {
            result.budgets = createBudgetsServices(httpClient, servicesConfig?.budgets);
        }
        // Create Google services (geocode / distance / places / directions) if
        // configured or by default. The microservice is opt-in: callers point
        // servicePath at CCMS.Api.GoogleServices.
        if (servicesConfig?.google !== undefined || servicesConfig === undefined) {
            result.google = createGoogleServices(httpClient, servicesConfig?.google);
        }
        // Create AI services if configured or by default
        if (servicesConfig?.ai !== undefined || servicesConfig === undefined) {
            result.ai = createAiServices(httpClient, servicesConfig?.ai);
        }
        // Create knowledge base services if configured or by default
        if (servicesConfig?.knowledgeBase !== undefined || servicesConfig === undefined) {
            result.knowledgeBase = createKnowledgeBaseServices(httpClient, servicesConfig?.knowledgeBase);
        }
        // Create analytics services if configured or by default
        if (servicesConfig?.analytics !== undefined || servicesConfig === undefined) {
            result.analytics = createAnalyticsServices(httpClient, servicesConfig?.analytics);
        }
        // Create application catalogue services if configured or by default
        if (servicesConfig?.applicationCatalogue !== undefined || servicesConfig === undefined) {
            result.applicationCatalogue = createApplicationCatalogueServices(httpClient, servicesConfig?.applicationCatalogue);
        }
        // Create LLM connector management services if configured or by default
        if (servicesConfig?.connector !== undefined || servicesConfig === undefined) {
            result.connector = createConnectorServices(httpClient, servicesConfig?.connector);
        }
        // Console-issued API keys (admin) — same conditions as the connector
        if (servicesConfig?.apiKeys !== undefined || servicesConfig === undefined) {
            result.apiKeys = createApiKeysServices(httpClient, servicesConfig?.apiKeys);
        }
        // Create Anatomy (Acts API) integration services if configured or by default
        if (servicesConfig?.anatomy !== undefined || servicesConfig === undefined) {
            result.anatomy = createAnatomyServices(httpClient, servicesConfig?.anatomy);
        }
        // Create Forms Management services if configured or by default
        if (servicesConfig?.forms !== undefined || servicesConfig === undefined) {
            result.forms = createFormsServices(httpClient, servicesConfig?.forms);
        }
        // Create Rides Management services if configured or by default
        if (servicesConfig?.rides !== undefined || servicesConfig === undefined) {
            result.rides = createRidesServices(httpClient, servicesConfig?.rides);
        }
        // Create Procedures Management services if configured or by default
        if (servicesConfig?.procedures !== undefined || servicesConfig === undefined) {
            result.procedures = createProceduresServices(httpClient, servicesConfig?.procedures);
        }
        // Create Call Centre Management services if configured or by default
        if (servicesConfig?.callCentre !== undefined || servicesConfig === undefined) {
            result.callCentre = createCallCentreServices(httpClient, servicesConfig?.callCentre);
        }
        // Create Salvation Management services if configured or by default
        if (servicesConfig?.salvation !== undefined || servicesConfig === undefined) {
            result.salvation = createSalvationServices(httpClient, servicesConfig?.salvation);
        }
        // Add other service groups here as they are implemented
        return result;
    }, [httpClient, servicesConfig]);
    // Context value
    const contextValue = useMemo(() => ({
        http: httpClient,
        presence: presenceClient,
        stock: stockClient,
        config,
        services,
    }), [httpClient, presenceClient, stockClient, config, services]);
    return (React.createElement(CCMSContext.Provider, { value: contextValue }, children));
}
/**
 * withCCMS HOC
 * Higher-order component to inject CCMS context
 */
export function withCCMS(Component) {
    return function WithCCMSComponent(props) {
        return (React.createElement(CCMSContext.Consumer, null, (context) => {
            if (!context) {
                throw new Error('withCCMS must be used within a CCMSProvider');
            }
            return React.createElement(Component, { ...props, ccms: context });
        }));
    };
}
//# sourceMappingURL=CCMSProvider.js.map